import {
	Instance,
	SnapshotIn,
	SnapshotOut,
	applySnapshot,
	flow,
	getParent,
	types,
} from 'mobx-state-tree';

import { getCgPartner, removeCgPartner, saveCgPartner } from '@common/api/cgPartners';
import { deleteMedia } from '@common/api/media';
import handleApiErrors from '@common/helpers/handleApiErrors';
import { ICreateCgPartner } from '@common/types/api/cgPartnersTypes';

import { ICgPartnersModelInstance } from './CgPartners';

type CgPartnerMetadataStatPayload = {
	value?: string | null;
	label?: string | null;
};

type CgPartnerMetadataRecommendationPayload = {
	heading?: string | null;
	text?: string | null;
};

type CgPartnerMetadataReviewPayload = {
	rating?: number | string | null;
	text?: string | null;
	author?: string | null;
};

type CgPartnerMetadataPayload = {
	stats?: CgPartnerMetadataStatPayload[] | null;
	recommendation?: CgPartnerMetadataRecommendationPayload | null;
	review?: CgPartnerMetadataReviewPayload | null;
};

type CgPartnerPayload = Omit<ICreateCgPartner, 'metadata'> & {
	metadata?: CgPartnerMetadataPayload | null;
};

const isEmptyValue = (value: unknown) =>
	value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const normalizeEmptyValue = <T>(value: T | null | undefined) =>
	isEmptyValue(value) ? undefined : value;

const normalizeStat = (stat: CgPartnerMetadataStatPayload) => {
	const value = normalizeEmptyValue(stat.value);
	const label = normalizeEmptyValue(stat.label);

	if (value === undefined && label === undefined) {
		return null;
	}

	return { value, label };
};

const normalizeRecommendation = (
	recommendation?: CgPartnerMetadataRecommendationPayload | null,
) => {
	const heading = normalizeEmptyValue(recommendation?.heading);
	const text = normalizeEmptyValue(recommendation?.text);

	if (heading === undefined && text === undefined) {
		return null;
	}

	return { heading, text };
};

const normalizeReview = (review?: CgPartnerMetadataReviewPayload | null) => {
	const rating =
		review?.rating === '' || review?.rating === null || review?.rating === undefined
			? 0
			: Number(review.rating);
	const text = normalizeEmptyValue(review?.text);
	const author = normalizeEmptyValue(review?.author);

	if (rating === 0 && text === undefined && author === undefined) {
		return null;
	}

	return { rating, text, author };
};

const normalizeMetadata = (metadata?: CgPartnerMetadataPayload | null) => {
	const stats = (metadata?.stats ?? [])
		.map(normalizeStat)
		.filter((stat): stat is NonNullable<ReturnType<typeof normalizeStat>> => stat !== null);
	const recommendation = normalizeRecommendation(metadata?.recommendation);
	const review = normalizeReview(metadata?.review);

	return {
		stats,
		recommendation,
		review,
	};
};

const normalizeCgPartnerPayload = (data: CgPartnerPayload) => ({
	...data,
	subtitle: normalizeEmptyValue(data.subtitle),
	description: normalizeEmptyValue(data.description),
	phone: normalizeEmptyValue(data.phone),
	logo_url: normalizeEmptyValue(data.logo_url),
	metadata: normalizeMetadata(data.metadata),
});

export const CgPartnerLogoModel = types.model('CgPartnerLogo', {
	url: types.maybeNull(types.string),
	original_name: types.maybeNull(types.string),
	size: types.maybeNull(types.number),
});

export const CgPartnerMetadataStatModel = types.model('CgPartnerMetadataStat', {
	label: types.maybeNull(types.string),
	value: types.maybeNull(types.string),
});

export const CgPartnerMetadataReviewModel = types.model('CgPartnerMetadataReview', {
	text: types.maybeNull(types.string),
	author: types.maybeNull(types.string),
	rating: types.number,
});

export const CgPartnerMetadataRecommendationModel = types.model('CgPartnerMetadataRecommendation', {
	text: types.maybeNull(types.string),
	heading: types.maybeNull(types.string),
});

export const CgPartnerMetadataModel = types.model('CgPartnerMetadata', {
	stats: types.optional(types.array(CgPartnerMetadataStatModel), []),
	review: types.maybeNull(CgPartnerMetadataReviewModel),
	recommendation: types.maybeNull(CgPartnerMetadataRecommendationModel),
});

export const CgPartnerSummaryModel = types.model('CgPartnerSummary', {
	id: types.identifierNumber,
	title: types.maybeNull(types.string),
	subtitle: types.maybeNull(types.string),
	description: types.maybeNull(types.string),
	is_internal: types.optional(types.boolean, false),
	logo_url: types.maybeNull(types.string),
});

const CgPartnerModel = types
	.compose(
		CgPartnerSummaryModel,
		types.model({
			metadata: types.maybeNull(CgPartnerMetadataModel),
			logo: types.maybeNull(CgPartnerLogoModel),
			products: types.optional(types.array(types.frozen()), []),
			product_ids: types.optional(types.array(types.number), []),
			phone: types.maybeNull(types.string),
			email: types.maybeNull(types.string),
			created_at: types.maybeNull(types.string),
			updated_at: types.maybeNull(types.string),
			user_id: types.maybeNull(types.number),
		}),
	)
	.named('CgPartner')
	.volatile(() => ({
		loading: false,
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getCgPartner(self.id);
				applySnapshot(self, normalizeCgPartnerPayload(result));
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield removeCgPartner(self.id);
				const parent = getParent<ICgPartnersModelInstance>(self, 2);
				parent.removeItem(self as ICgPartnerModelInstance);
			} catch (e) {
				self.loading = false;
			}
		}),
		save: flow(function* (data: ICreateCgPartner) {
			self.loading = true;
			try {
				const result = yield saveCgPartner(self.id, normalizeCgPartnerPayload(data));
				applySnapshot(self, normalizeCgPartnerPayload(result));
			} catch (e) {
				handleApiErrors(e);
			} finally {
				self.loading = false;
			}
		}),
		deleteAvatar: flow(function* () {
			yield deleteMedia({
				id: self.user_id as number,
				entity: 'users',
				media_collection: 'avatar',
			});
			// yield self._fetch(self.id);
		}),
	}));

export default CgPartnerModel;

export type ICgPartnerModelInstance = Instance<typeof CgPartnerModel>;
export type ICgPartnerModelSnapshotIn = SnapshotIn<typeof CgPartnerModel>;
export type ICgPartnerModelSnapshotOut = SnapshotOut<typeof CgPartnerModel>;

export type ICgPartnerSummaryModelInstance = Instance<typeof CgPartnerSummaryModel>;
export type ICgPartnerSummaryModelSnapshotIn = SnapshotIn<typeof CgPartnerSummaryModel>;
export type ICgPartnerSummaryModelSnapshotOut = SnapshotOut<typeof CgPartnerSummaryModel>;
